/**
 * Cart Kit Mapping
 * Reads rules from #rg-kit-transform-config (global Overlay section).
 * Mounts: [data-kit-transform] on cart page and cart drawer.
 */
(() => {
  if (window.__rgKitTransformInit) return;
  window.__rgKitTransformInit = true;

  const CONFIG_ID = 'rg-kit-transform-config';
  const STORAGE_KEY = 'rg-kit-transform';
  const MASK_CLASS = 'rg-kit-transform-mask';

  const routes = (window.theme && window.theme.routes) || {};
  const cartUrl = routes.cart || '/cart';
  const cartAddUrl = routes.cartAdd || `${cartUrl}/add`;
  const cartUpdateUrl = routes.cartUpdate || `${cartUrl}/update`;

  let config = null;
  let isBusy = false;
  let maskEl = null;

  function getConfig() {
    if (config) return config;
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      config = JSON.parse(el.textContent.trim());
    } catch (e) {
      console.warn('[kit-transform] invalid config JSON', e);
      config = null;
    }
    return config;
  }

  function getMounts() {
    return Array.from(document.querySelectorAll('[data-kit-transform]'));
  }

  function readSession() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeSession(data) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  function clearSession() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  function normalizeCartEndpoint(url) {
    if (!url) return url;
    if (url.endsWith('.js')) return url;
    return `${url}.js`;
  }

  async function fetchCart() {
    const res = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  async function postCartAdd(items) {
    const url = normalizeCartEndpoint(cartAddUrl);
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || data.errors || `add ${res.status}`);
    }
    return data;
  }

  async function postCartUpdate(updates) {
    const url = normalizeCartEndpoint(cartUpdateUrl);
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || data.errors || `update ${res.status}`);
    }
    return data;
  }

  function aggregateCartBySku(cart) {
    const map = {};
    const linesBySku = {};
    let missingSku = false;
    (cart.items || []).forEach((item) => {
      const sku = String(item.sku || '').trim();
      if (!sku) {
        missingSku = true;
        return;
      }
      const key = sku.toLowerCase();
      map[key] = (map[key] || 0) + Number(item.quantity || 0);
      if (!linesBySku[key]) linesBySku[key] = [];
      linesBySku[key].push(item);
    });
    return { map, linesBySku, missingSku, itemCount: cart.item_count || 0, items: cart.items || [] };
  }

  function skuKey(sku) {
    return String(sku || '').trim().toLowerCase();
  }

  /**
   * Strict recipe match: cart SKU set equals recipe; same integer multiplier N.
   */
  function matchChildrenRecipe(cartAgg, mapConfig) {
    if (cartAgg.missingSku) return null;

    const recipe = {};
    (mapConfig.children || []).forEach((c) => {
      const key = skuKey(c.sku);
      const qty = Number(c.qty) || 0;
      if (!key || qty < 1) return;
      recipe[key] = qty;
    });

    const recipeSkus = Object.keys(recipe);
    if (!recipeSkus.length) return null;

    const cartSkus = Object.keys(cartAgg.map);
    if (cartSkus.length !== recipeSkus.length) return null;
    if (!recipeSkus.every((s) => cartAgg.map[s] != null)) return null;
    if (!cartSkus.every((s) => recipe[s] != null)) return null;

    let multiplier = null;
    for (let i = 0; i < recipeSkus.length; i += 1) {
      const key = recipeSkus[i];
      const cartQty = cartAgg.map[key];
      const per = recipe[key];
      if (cartQty % per !== 0) return null;
      const n = cartQty / per;
      if (n < 1 || !Number.isInteger(n)) return null;
      if (multiplier === null) multiplier = n;
      else if (multiplier !== n) return null;
    }

    const childrenFromCart = recipeSkus.map((key) => {
      const lines = cartAgg.linesBySku[key] || [];
      const variantId = lines[0] && lines[0].variant_id;
      const originalSku = (lines[0] && lines[0].sku) || key;
      return {
        sku: originalSku,
        qty: recipe[key],
        variantId: Number(variantId)
      };
    });

    if (childrenFromCart.some((c) => !c.variantId)) return null;

    return { mapConfig, multiplier, childrenFromCart };
  }

  /**
   * Find kit lines by variant id and/or sku. Allows other cart lines for undo UI.
   */
  function findKitInCart(cart, session) {
    if (!session) return null;
    const items = cart.items || [];
    const kitVariantId = Number(session.kitVariantId);
    const kitSku = skuKey(session.kitSku);
    const multiplier = Number(session.multiplier) || 0;
    if (!multiplier) return null;

    let lines = [];
    if (kitVariantId) {
      lines = items.filter((i) => Number(i.variant_id) === kitVariantId);
    }
    if (!lines.length && kitSku) {
      lines = items.filter((i) => skuKey(i.sku) === kitSku);
    }
    if (!lines.length) return null;

    const qty = lines.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
    if (qty < multiplier) return null;

    return {
      multiplier,
      kitVariantId: Number(lines[0].variant_id),
      kitQty: qty,
      lines
    };
  }

  /**
   * Strict: cart SKUs are exactly the kit only (used after convert when no extras).
   */
  function matchKitOnly(cartAgg, kitSku, expectedN) {
    if (cartAgg.missingSku) return null;
    const key = skuKey(kitSku);
    const skus = Object.keys(cartAgg.map);
    if (skus.length !== 1) return null;
    if (skus[0] !== key) return null;
    const qty = cartAgg.map[key];
    if (expectedN != null && qty !== expectedN) return null;
    if (qty < 1) return null;
    const lines = cartAgg.linesBySku[key] || [];
    const variantId = lines[0] && lines[0].variant_id;
    if (!variantId) return null;
    return { multiplier: qty, kitVariantId: Number(variantId), lines };
  }

  function findFirstChildMatch(cartAgg, maps) {
    for (let i = 0; i < (maps || []).length; i += 1) {
      const hit = matchChildrenRecipe(cartAgg, maps[i]);
      if (hit) return hit;
    }
    return null;
  }

  function findMapByKitSku(maps, kitSku) {
    const key = skuKey(kitSku);
    return (maps || []).find((m) => skuKey(m.kitSku) === key) || null;
  }

  async function resolveKitVariantId(kitSku) {
    const sku = String(kitSku || '').trim();
    if (!sku) throw new Error('Missing kit SKU');

    const fields = 'title,variants.sku,variants.title';
    const params = new URLSearchParams();
    params.set('q', sku);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][fields]', fields);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const suggestUrl = `${root}search/suggest.json?${params.toString()}`;
    const res = await fetch(suggestUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error(`suggest ${res.status}`);
    const data = await res.json();
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];

    for (let i = 0; i < products.length; i += 1) {
      const handle = products[i].handle;
      if (!handle) continue;
      const prodRes = await fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' });
      if (!prodRes.ok) continue;
      const product = await prodRes.json();
      const variant = (product.variants || []).find((v) => String(v.sku || '').trim() === sku);
      if (variant && variant.id) return Number(variant.id);
    }

    throw new Error(`Kit SKU not found: ${sku}`);
  }

  function showMask() {
    if (maskEl) return;
    maskEl = document.createElement('div');
    maskEl.className = MASK_CLASS;
    maskEl.setAttribute('aria-busy', 'true');
    maskEl.setAttribute('role', 'presentation');
    maskEl.innerHTML = '<div class="rg-kit-transform-mask__spinner" aria-hidden="true"></div>';
    document.body.appendChild(maskEl);
    document.body.classList.add('rg-kit-transform-busy');
  }

  function hideMask() {
    if (maskEl && maskEl.parentNode) maskEl.parentNode.removeChild(maskEl);
    maskEl = null;
    document.body.classList.remove('rg-kit-transform-busy');
  }

  function showMountError(message) {
    getMounts().forEach((root) => {
      let err = root.querySelector('[data-kit-error]');
      if (!err) {
        err = document.createElement('div');
        err.className = 'kit-transform__error';
        err.setAttribute('data-kit-error', '');
        root.appendChild(err);
      }
      err.textContent = message || '';
      err.hidden = !message;
    });
  }

  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { detail: { open: false } }));
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      cartItems.refresh();
    }
  }

  function applyImage(root, mapConfig) {
    const img = root.querySelector('[data-kit-img]');
    const sourceM = root.querySelector('[data-kit-img-m]');
    if (!img) return;
    const pc = mapConfig.imagePc || '';
    const m = mapConfig.imageM || pc || '';
    img.src = pc || m;
    img.alt = '';
    if (sourceM) {
      if (m) sourceM.setAttribute('srcset', m);
      else sourceM.removeAttribute('srcset');
    }
    img.hidden = !(pc || m);
  }

  function setVisible(root, visible) {
    if (visible) root.removeAttribute('hidden');
    else root.setAttribute('hidden', '');
  }

  function enhanceMatchHtml(html) {
    if (!html) return '';
    return String(html).replace(
      /(Save\s*\$\d[\d,]*)/gi,
      '<span style="color:#E04700">$1</span>'
    );
  }

  function renderMatch(root, cfg, mapConfig) {
    const textEl = root.querySelector('[data-kit-text]');
    const convertBtn = root.querySelector('[data-kit-convert]');
    const undoBtn = root.querySelector('[data-kit-undo]');
    const card = root.querySelector('[data-kit-card]');

    if (card) card.style.setProperty('--kit-bg', cfg.bgColor || '#F6F6F6');
    applyImage(root, mapConfig);
    if (textEl) textEl.innerHTML = enhanceMatchHtml(mapConfig.matchText || '');

    if (convertBtn) {
      convertBtn.textContent = cfg.convertBtnText || 'Add Bundle & Replace Items';
      convertBtn.hidden = false;
    }
    if (undoBtn) undoBtn.hidden = true;
    setVisible(root, true);
  }

  function renderUndo(root, cfg, mapConfig) {
    const textEl = root.querySelector('[data-kit-text]');
    const convertBtn = root.querySelector('[data-kit-convert]');
    const undoBtn = root.querySelector('[data-kit-undo]');
    const undoLabel = root.querySelector('[data-kit-undo-label]');
    const card = root.querySelector('[data-kit-card]');

    if (card) card.style.setProperty('--kit-bg', cfg.bgColor || '#F6F6F6');
    applyImage(root, mapConfig);
    if (textEl) textEl.innerHTML = mapConfig.undoText || '';

    if (convertBtn) convertBtn.hidden = true;
    if (undoBtn) {
      undoBtn.hidden = false;
      if (undoLabel) undoLabel.textContent = cfg.undoBtnText || 'Undo';
    }
    setVisible(root, true);
  }

  function hideAllMounts() {
    getMounts().forEach((root) => setVisible(root, false));
  }

  async function evaluate() {
    const cfg = getConfig();
    if (!cfg || !Array.isArray(cfg.maps) || !cfg.maps.length) {
      hideAllMounts();
      return;
    }

    const mounts = getMounts();
    if (!mounts.length) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      hideAllMounts();
      return;
    }

    if (!cart.item_count) {
      hideAllMounts();
      return;
    }

    const cartAgg = aggregateCartBySku(cart);
    const session = readSession();

    // Undo UI: session exists and kit is still in cart
    if (session && session.kitSku && session.multiplier) {
      const kitHit = findKitInCart(cart, session) || matchKitOnly(cartAgg, session.kitSku, session.multiplier);
      const mapConfig = findMapByKitSku(cfg.maps, session.kitSku);
      if (kitHit && mapConfig) {
        mounts.forEach((root) => renderUndo(root, cfg, mapConfig));
        return;
      }
      if (!kitHit) clearSession();
    }

    const childHit = findFirstChildMatch(cartAgg, cfg.maps);
    if (childHit) {
      mounts.forEach((root) => {
        root._kitMatch = childHit;
        renderMatch(root, cfg, childHit.mapConfig);
      });
      return;
    }

    hideAllMounts();
  }

  async function convertFromMatch(match) {
    if (!match) throw new Error('No match');
    const { mapConfig, multiplier, childrenFromCart } = match;
    const kitSku = String(mapConfig.kitSku || '').trim();

    const cart = await fetchCart();
    const cartAgg = aggregateCartBySku(cart);
    const recheck = matchChildrenRecipe(cartAgg, mapConfig);
    if (!recheck || recheck.multiplier !== multiplier) {
      throw new Error('Cart no longer matches kit recipe');
    }

    const kitVariantId = await resolveKitVariantId(kitSku);

    // 1) Add kit first
    await postCartAdd([{ id: kitVariantId, quantity: multiplier }]);

    // 2) Remove children by variant id
    const cartAfterAdd = await fetchCart();
    const updates = {};
    childrenFromCart.forEach((c) => {
      (cartAfterAdd.items || []).forEach((line) => {
        if (Number(line.variant_id) === Number(c.variantId)) {
          updates[String(line.variant_id)] = 0;
        }
        // Also match by sku in case variant id lines differ
        if (skuKey(line.sku) === skuKey(c.sku) && Number(line.variant_id) !== Number(kitVariantId)) {
          updates[String(line.variant_id)] = 0;
        }
      });
    });

    if (!Object.keys(updates).length) {
      // Rollback kit
      try {
        await postCartUpdate({ [String(kitVariantId)]: 0 });
      } catch (e) {
        console.error('[kit-transform] rollback kit failed', e);
      }
      throw new Error('Could not remove child items');
    }

    try {
      await postCartUpdate(updates);
    } catch (err) {
      try {
        await postCartUpdate({ [String(kitVariantId)]: 0 });
      } catch (rollbackErr) {
        console.error('[kit-transform] rollback failed', rollbackErr);
      }
      throw err;
    }

    writeSession({
      kitSku,
      multiplier,
      children: childrenFromCart,
      kitVariantId: Number(kitVariantId)
    });
  }

  async function undoConversion() {
    const session = readSession();
    if (!session || !session.multiplier || !Array.isArray(session.children) || !session.children.length) {
      throw new Error('No session conversion to undo');
    }

    const cart = await fetchCart();
    const kitHit = findKitInCart(cart, session);
    if (!kitHit) {
      clearSession();
      throw new Error('Kit not found in cart — cannot undo');
    }

    const addItems = session.children
      .filter((c) => c.variantId)
      .map((c) => ({
        id: Number(c.variantId),
        quantity: Number(c.qty) * Number(session.multiplier)
      }));

    if (!addItems.length) throw new Error('Missing child variant ids in session');

    // 1) Restore children first
    await postCartAdd(addItems);

    // 2) Remove kit qty that was converted
    const newKitQty = Math.max(0, Number(kitHit.kitQty) - Number(session.multiplier));
    const kitVariantId = Number(kitHit.kitVariantId || session.kitVariantId);

    try {
      await postCartUpdate({ [String(kitVariantId)]: newKitQty });
    } catch (err) {
      // Rollback restored children
      try {
        const rollback = {};
        session.children.forEach((c) => {
          if (c.variantId) rollback[String(c.variantId)] = 0;
        });
        await postCartUpdate(rollback);
      } catch (rollbackErr) {
        console.error('[kit-transform] undo rollback failed', rollbackErr);
      }
      throw err;
    }

    clearSession();
  }

  async function withBusy(fn) {
    if (isBusy) return;
    isBusy = true;
    showMask();
    showMountError('');
    try {
      await fn();
      await evaluate();
      refreshCartUi();
      window.setTimeout(() => {
        evaluate().catch(() => {});
      }, 500);
    } catch (err) {
      console.error('[kit-transform]', err);
      showMountError(err && err.message ? err.message : 'Kit transform failed');
      await evaluate();
    } finally {
      hideMask();
      isBusy = false;
    }
  }

  async function handleConvertClick() {
    await withBusy(async () => {
      const cfg = getConfig();
      if (!cfg) throw new Error('Missing kit config');
      const cart = await fetchCart();
      const cartAgg = aggregateCartBySku(cart);
      const match = findFirstChildMatch(cartAgg, cfg.maps);
      if (!match) throw new Error('Cart no longer matches kit recipe');
      await convertFromMatch(match);
    });
  }

  async function handleUndoClick() {
    await withBusy(() => undoConversion());
  }

  // Event delegation — survives cart drawer / section HTML refresh
  document.addEventListener(
    'click',
    (e) => {
      const undoBtn = e.target.closest('[data-kit-undo]');
      const convertBtn = e.target.closest('[data-kit-convert]');
      if (!undoBtn && !convertBtn) return;
      if (!e.target.closest('[data-kit-transform]')) return;

      e.preventDefault();
      e.stopPropagation();

      if (undoBtn && !undoBtn.hidden) {
        handleUndoClick();
        return;
      }
      if (convertBtn && !convertBtn.hidden) {
        handleConvertClick();
      }
    },
    true
  );

  function init() {
    if (!getConfig()) return;
    evaluate();
  }

  document.addEventListener('on:line-item:change', () => {
    evaluate();
  });

  document.addEventListener('dispatch:cart-drawer:refresh', () => {
    window.setTimeout(() => {
      evaluate();
    }, 350);
  });

  document.addEventListener('dispatch:cart-drawer:open', () => {
    window.setTimeout(() => {
      evaluate();
    }, 80);
  });

  document.addEventListener('on:cart-drawer:render', () => {
    window.setTimeout(() => {
      evaluate();
    }, 80);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
